import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { VISIT_COST_OPTION } from '@features/patient-group-dropdown/constants/visit-cost-option.const';

import {
  injectProtocolDiscontinuationRate,
  injectRemoveProtocolDiscontinuationRate,
  injectSetProtocolDiscontinuationRate,
} from '@models/patient-protocol/api/patient-protocol-discontinuation-rate.api';

import { InputMaskComponent } from '@shared/components/input-mask/input-mask.component';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { OverlayService } from '@shared/services/overlay.service';
import { decimalDivide, decimalMultiply } from '@shared/utils';

@Component({
  imports: [InputMaskComponent, ReactiveFormsModule, SkeletonLoaderDirective],
  selector: 'aux-discontinuation-rate-input',
  template: `
    <div>
      <aux-input-mask
        [formControl]="rateFc"
        maskType="percentage"
        label="Overall Discontinuation Rate (%)"
        placeholder="0%"
        [precision]="2"
        [validators]="discontinuationRateInputValidators"
        [customErrorMessages]="{
          min: discontinuationValidationMessage,
          max: discontinuationValidationMessage,
        }"
        auxSkeletonLoader
        [loading]="isLoading()"
      />
    </div>
  `,
})
export class DiscontinuationRateInputComponent {
  private overlayService = inject(OverlayService);

  patientGroup = input.required<string>();
  protocolVersionId = input.required<string>();

  private overallDiscontinuationRate = injectProtocolDiscontinuationRate({
    patientGroup: this.patientGroup,
    protocolVersionId: this.protocolVersionId,
  });
  private removeProtocolDiscontinuationRate = injectRemoveProtocolDiscontinuationRate();
  private setProtocolDiscontinuationRate = injectSetProtocolDiscontinuationRate();

  initialValue = signal<null | number>(null);
  isLoading = signal(false);

  rateFc = new FormControl<null | number>(null);

  value = toSignal(this.rateFc.valueChanges);

  hasChanges = computed(() => {
    return this.value() !== this.initialValue();
  });

  disabled = input(false);

  _setDefaultDiscontinuationRate = effect(() => {
    const overallDiscontinuationRate =
      this.overallDiscontinuationRate()?.data?.discontinuation_rate ?? null;

    const value =
      overallDiscontinuationRate !== null
        ? decimalMultiply(overallDiscontinuationRate, 100, 4)
        : null;

    this.rateFc.setValue(value);
    this.initialValue.set(this.rateFc.value ?? null);
  });
  _toggleDisabled = effect(() => {
    if (this.disabled()) {
      this.rateFc.disable();
      this.onCancel();
    } else {
      this.rateFc.enable();
    }
  });
  discontinuationRateInputValidators = [Validators.min(0), Validators.max(99.99)];
  discontinuationValidationMessage = 'Discontinuation rate value must be from 0% to less than 100%';

  isSaveDisabled() {
    return this.rateFc.invalid;
  }

  onCancel() {
    this.rateFc.setValue(this.initialValue());
  }

  async onSave() {
    if (this.isSaveDisabled() || !this.shouldUpdateDiscontinuationRate()) return;

    this.isLoading.set(true);
    try {
      await this.updateDiscontinuationRate();

      this.initialValue.set(this.rateFc.value);
      this.overlayService.success('Successfully saved!');
    } catch (error) {
      this.overlayService.error('Failed to update discontinuation rate');
      this.rateFc.setValue(this.initialValue());
      console.error('Failed to update discontinuation rate:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  shouldUpdateDiscontinuationRate() {
    return this.rateFc.value !== this.initialValue();
  }

  private updateDiscontinuationRate() {
    if (this.isSaveDisabled() || !this.shouldUpdateDiscontinuationRate()) return;

    const patientGroup =
      VISIT_COST_OPTION.value === this.patientGroup() ? null : this.patientGroup();

    if (!this.rateFc.value) {
      return this.removeProtocolDiscontinuationRate.mutateAsync({
        patient_group_id: patientGroup,
        protocol_version_id: this.protocolVersionId(),
      });
    }

    return this.setProtocolDiscontinuationRate.mutateAsync({
      discontinuation_rate: decimalDivide(this.rateFc.value!, 100),
      patient_group_id: patientGroup,
      protocol_version_id: this.protocolVersionId(),
    });
  }
}
